// src/App.tsx
import "./App.css";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, useState, useEffect } from "react";
import { Drawer, Button } from "antd";
import { MenuOutlined } from "@ant-design/icons";

import AppRoutes from "./routes/AppRoutes";
import Sidebar from "./components/Sidebar";
import ErrorBoundary from "./ui/ErrorBoundary";
import AppErrorFallback from "./ui/AppErrorFallback";
import { useIsMobile } from "./hooks/useIsMobile";
import logo from "./assets/logo.png";

import Login from "./pages/Login";
import Logout from "./pages/Logout";
import PrivateRoute from "./routes/PrivateRoute";
import PublicOnlyRoute from "./routes/PublicOnlyRoute";

function Layout() {
  const location = useLocation(); // ключ для ErrorBoundary, чтобы сбрасывать состояние на смене пути
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Закрываем выезжающее меню при переходе на другую страницу
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <ErrorBoundary
      boundaryKey={location.pathname}
      fallbackRender={({ error, reset }) => <AppErrorFallback error={error} reset={reset} />}
      onError={(err) => {
        // eslint-disable-next-line no-console
        console.error("[AppErrorBoundary]", err);
      }}
    >
      {isMobile ? (
        // Мобильный макет: сайдбар прячется в выезжающую панель, контент — на всю ширину
        <div style={{ minHeight: "100vh" }}>
          <div
            style={{
              position: "sticky", top: 0, zIndex: 50,
              display: "flex", alignItems: "center", gap: 12,
              padding: "6px 12px", background: "#1f1f1f",
              borderBottom: "2px solid #b70000",
            }}
          >
            <Button
              type="text"
              aria-label="Меню"
              icon={<MenuOutlined style={{ fontSize: 22, color: "#fff" }} />}
              onClick={() => setDrawerOpen(true)}
            />
            <img src={logo} alt="Svecha" style={{ height: 30, width: "auto" }} />
          </div>

          <Drawer
            placement="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            width={300}
            closable={false}
            styles={{ body: { padding: 0 } }}
          >
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </Drawer>

          <div style={{ minWidth: 0, overflowX: "hidden" }}>
            <AppRoutes />
          </div>
        </div>
      ) : (
        // Десктоп — без изменений
        <div style={{ display: "flex", minHeight: "100vh" }}>
          <Sidebar />
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <AppRoutes />
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
}

const App: React.FC = () => {
  return (
      <Suspense fallback={<div style={{ padding: 20 }}>Загрузка…</div>}>
        <Routes>
          {/* Публичная зона — только для НЕавторизованных */}
          <Route element={<PublicOnlyRoute />}>
            <Route path="/login" element={<Login />} />
          </Route>

          {/* /logout доступен всегда (как тебе удобнее) */}
          <Route path="/logout" element={<Logout />} />

          {/* Приватная зона с лейаутом (сайдбар внутри) */}
          <Route element={<PrivateRoute />}>
            <Route path="/*" element={<Layout />} />
          </Route>

          {/* Фоллбек на неизвестные пути */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
  );
};

export default App;
